'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import styles from './styles/tech-slider.module.css'

export const RunSafeInTheDarkTechSliderMobile = () => {
  const t = useTranslations()

  const problems = [
    {
      image: '/image/2026/09/18/Frame_880722.jpg',
      title: t('safe_in_the_dark_hardcode.tech_slider.slide_1.problem_1_title'),
      description: t(
        'safe_in_the_dark_hardcode.tech_slider.slide_1.problem_1_description'
      ),
      overlay: 'bg-neutral-900/40',
    },
    {
      image: '/image/2026/09/18/Frame_88073112.jpg',
      title: t('safe_in_the_dark_hardcode.tech_slider.slide_1.problem_2_title'),
      description: t(
        'safe_in_the_dark_hardcode.tech_slider.slide_1.problem_2_description'
      ),
      overlay: 'bg-primary/40 mix-blend-color',
    },
  ]

  const movementCards = [
    {
      image: '/image/2026/09/18/image.jpg',
      title: null as string | null,
      description: t('safe_in_the_dark_hardcode.tech_slider.slide_3.item_1'),
    },
    {
      image: '/image/2026/09/18/image-1.jpg',
      title: null as string | null,
      description: t('safe_in_the_dark_hardcode.tech_slider.slide_3.item_2'),
    },
    {
      image: '/image/2026/09/14/Shape_retention.jpg',
      title: t(
        'safe_in_the_dark_hardcode.outfit_highlights.shape_retention.title'
      ),
      description: t(
        'safe_in_the_dark_hardcode.outfit_highlights.shape_retention.description'
      ),
    },
  ]

  return (
    <div className='flex flex-col gap-10 overflow-hidden bg-neutral-900 px-5 py-10'>
      {/* Section 1 — In the dark, being seen matters */}
      <div className='flex flex-col items-center gap-6'>
        <h2 className='text-center font-criteria text-2xl uppercase leading-tight text-white'>
          <span className='font-extralight'>
            {t('safe_in_the_dark_hardcode.tech_slider.slide_1.title_line_1')}
          </span>
          <br />
          <span className={cn(styles.textGlowStrong, 'font-medium')}>
            {t('safe_in_the_dark_hardcode.tech_slider.slide_1.title_line_2')}
          </span>
        </h2>

        <div className='flex w-full flex-col overflow-hidden rounded-2xl'>
          {problems.map((problem, index) => (
            <div key={index} className='relative aspect-[375/280] w-full'>
              <Image
                src={ImageHelper.getImageUrlCdn(problem.image)}
                alt={problem.title}
                fill
                className='object-cover'
              />
              <div className={cn('absolute inset-0', problem.overlay)} />
              <div className='absolute inset-x-0 bottom-0 flex flex-col items-center gap-1 p-5 pb-6 text-center text-white'>
                <h3
                  className={cn(
                    styles.textGlowStrong,
                    'font-criteria text-lg font-bold uppercase leading-tight'
                  )}
                >
                  {problem.title}
                </h3>
                <p className='text-sm text-neutral-200'>
                  {problem.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className='flex flex-col items-center gap-2 text-center'>
          <div className='h-1 w-[100px] rounded-full bg-gradient-to-r from-neutral-700 to-[#A291FF]' />
          <h2 className='bg-gradient-to-r from-[#a291ff] to-white bg-clip-text font-criteria text-2xl font-semibold text-transparent'>
            {t('safe_in_the_dark_hardcode.tech_slider.slide_1.tagline')}
          </h2>
          <p className='text-base text-neutral-300'>
            {t(
              'safe_in_the_dark_hardcode.tech_slider.slide_1.tagline_description'
            )}
          </p>
        </div>
      </div>

      {/* Section 2 — Công nghệ 360Reflect */}
      <div className='relative flex flex-col gap-6'>
        <div className={cn(styles.glowBlob, styles.glowBlobMobileTopLeft)} />

        <div className='relative w-full'>
          <h2
            className={cn(
              styles.textGlowStrong,
              'font-criteria text-4xl font-semibold uppercase leading-tight text-white'
            )}
          >
            {t.rich('safe_in_the_dark_hardcode.highlight_1.title', {
              br: () => <br />,
              tm: (chunks) => <span className='font-sans'>{chunks}</span>,
            })}
          </h2>
          <p className='mt-2 font-criteria text-lg text-light'>
            {t('safe_in_the_dark_hardcode.highlight_1.sub_heading')}
          </p>
          <p className='mt-3 text-sm leading-6 text-neutral-200'>
            {t.rich('safe_in_the_dark_hardcode.highlight_1.description', {
              b: (chunks) => <b className='text-white'>{chunks}</b>,
            })}
          </p>
        </div>
        <div className='relative aspect-[985/603] w-full overflow-hidden rounded-2xl'>
          <Image
            src={ImageHelper.getImageUrlCdn(
              '/image/2026/09/18/360reflect-tech.png'
            )}
            alt={t('safe_in_the_dark_hardcode.highlight_1.sub_heading')}
            fill
            className='object-cover'
          />
        </div>
      </div>

      {/* Section 3 — Light follows your movement */}
      <div className='flex flex-col gap-6'>
        <h2
          className={cn(
            styles.textGlowStrong,
            'text-center font-criteria text-2xl font-semibold uppercase text-white'
          )}
        >
          {t('safe_in_the_dark_hardcode.tech_slider.slide_3.title')}
        </h2>

        <Carousel
          opts={{ align: 'start', slidesToScroll: 1, dragFree: true }}
          className='w-full'
        >
          <CarouselContent className='-ml-3'>
            {movementCards.map((item, index) => (
              <CarouselItem key={index} className='basis-auto pl-3'>
                <div className='relative h-[269px] w-[210px] overflow-hidden rounded-2xl'>
                  <Image
                    src={ImageHelper.getImageUrlCdn(item.image)}
                    alt={item.title ?? item.description}
                    fill
                    className='object-cover'
                  />
                  <div className='absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 to-transparent' />
                  <div className='absolute bottom-3 left-0 right-0 flex flex-col gap-1 px-3'>
                    {item.title && (
                      <p className='font-criteria text-base uppercase text-white'>
                        {item.title}
                      </p>
                    )}
                    <p className='text-xs text-white'>{item.description}</p>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  )
}
